import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import Label from "../label";
import MessageError from "../../message/__errors";
import "../styles.css";
import "./styles.css";

// Кастомный селект (НЕ нативный). options — ОБЪЕКТ {id: label} (формат state.option.*) либо
// упорядоченный МАССИВ пар [[id, label], …] — когда порядок вариантов важен: у объекта с числовыми
// ключами JS сам переставляет их по возрастанию id (напр. прокси по региону — см. devices ProxyModal),
// onChange получает {target: {value: id}}; isNull добавляет пункт «Не выбрано» (value '');
// есть поиск по подписи и закрытие по клику вне.
// Значение опции может быть и строкой (обычный случай), и объектом {label, disabled, hint, header} —
// disabled/hint нужны точечно (напр. занятый другим устройством аккаунт на странице «Устройства»):
// disabled запрещает выбор (клик по такой опции — no-op), hint дорисовывается после label
// приглушённым цветом прямо в строке варианта.
// header: true — заголовок группы (optgroup): не выбирается, поиск по заголовку оставляет всю группу.
function optionMeta(raw) {
    if (raw !== null && typeof raw === 'object') {
        const header = !!raw.header;
        return { label: raw.label, disabled: !!raw.disabled || header, hint: raw.hint || '', header };
    }
    return { label: raw, disabled: false, hint: '', header: false };
}

function matchesSearch(meta, searchLower) {
    if (!searchLower) return true;
    return String(meta.label).toLowerCase().includes(searchLower)
        || String(meta.hint).toLowerCase().includes(searchLower);
}

// Поиск с учётом заголовков групп: совпал заголовок — видна вся группа; совпали пункты —
// заголовок остаётся над ними. Без заголовков поведение прежнее (фильтр по подписи).
function filterOptions(entries, searchLower) {
    const mapped = entries.map(([id, raw]) => [id, optionMeta(raw)]);
    if (!searchLower) return mapped;

    const out = [];
    let header = null;
    let items = [];

    const flush = () => {
        if (!header && items.length === 0) return;
        const headerHit = header ? matchesSearch(header[1], searchLower) : false;
        const kept = headerHit ? items : items.filter(([, meta]) => matchesSearch(meta, searchLower));
        if (headerHit || kept.length > 0) {
            if (header) out.push(header);
            out.push(...kept);
        }
        header = null;
        items = [];
    };

    mapped.forEach((entry) => {
        if (entry[1].header) {
            flush();
            header = entry;
            return;
        }
        items.push(entry);
    });
    flush();
    return out;
}

// Дропдаун рендерится порталом в document.body (position: fixed по координатам поля) —
// иначе внутри <table> абсолютно спозиционированный дропдаун раздувал высоту строки
// (баг table-layout в браузерах: ячейка учитывает высоту абсолютных потомков).
export default function Select({
    options = {},
    value = '',
    onChange,
    readOnly = false,
    isNull = false,
    children,
    errors,
    className = '',
    hasLabel = true,
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [dropdownStyle, setDropdownStyle] = useState(null);
    const rootRef = useRef(null);
    const valueRef = useRef(null);
    const dropdownRef = useRef(null);

    // Позиция дропдауна во вьюпорте. Просто «под полем» мало: у панели массовых действий
    // (устройства, выбор чекбоксами) поле стоит внизу экрана — список уезжал за нижний край,
    // и нижние варианты нельзя было выбрать. Поэтому: считаем место снизу/сверху, при нехватке
    // раскрываем ВВЕРХ, и в любом случае отдаём дропдауну maxHeight по факту свободного места
    // (внутренний скролл списка). По горизонтали держим в пределах экрана — на телефоне поле
    // у правого края иначе выносило дропдаун за вьюпорт.
    const updatePosition = useCallback(() => {
        if (!valueRef.current) return;
        const rect = valueRef.current.getBoundingClientRect();
        const margin = 8;
        const viewportH = window.innerHeight;
        const viewportW = window.innerWidth;

        const spaceBelow = viewportH - rect.bottom - margin - 4;
        const spaceAbove = rect.top - margin - 4;
        // Вверх — только если снизу реально тесно (меньше 180px) и сверху просторнее.
        const openUp = spaceBelow < 180 && spaceAbove > spaceBelow;
        const maxHeight = Math.max(140, Math.min(320, openUp ? spaceAbove : spaceBelow));

        const width = Math.min(Math.max(rect.width, 200), viewportW - margin * 2);
        const left = Math.max(margin, Math.min(rect.left, viewportW - width - margin));

        setDropdownStyle(openUp
            ? { position: 'fixed', bottom: viewportH - rect.top + 4, left, width, maxHeight }
            : { position: 'fixed', top: rect.bottom + 4, left, width, maxHeight });
    }, []);

    // Закрытие по клику вне компонента (учитывая, что дропдаун вынесен порталом в body)
    useEffect(() => {
        if (!isOpen) return undefined;
        updatePosition();
        const handleClickOutside = (event) => {
            if (rootRef.current && rootRef.current.contains(event.target)) return;
            if (dropdownRef.current && dropdownRef.current.contains(event.target)) return;
            setIsOpen(false);
            setSearch('');
        };
        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [isOpen, updatePosition]);

    // Пары [id, значение] в порядке показа; id — строкой, как value из onChange.
    const optionEntries = (Array.isArray(options) ? options : Object.entries(options))
        .map(([id, raw]) => [String(id), raw]);
    const optionsById = Object.fromEntries(optionEntries);

    const currentLabel = ((value !== '') && (value !== null) && (value !== undefined) && (String(value) in optionsById))
        ? optionMeta(optionsById[String(value)]).label
        : (isNull ? 'Не выбрано' : '');

    const handleSelect = (id, disabled) => {
        if (disabled) return;
        if (onChange) onChange({ target: { value: id } });
        setIsOpen(false);
        setSearch('');
    };

    const filteredOptions = filterOptions(optionEntries, search.toLowerCase());

    return <>
        {hasLabel ? <Label>{children}</Label> : null}
        <div className={`select${readOnly ? ' select_readonly' : ''}`} ref={rootRef}>
            <div
                ref={valueRef}
                className={`input select__value ${className}`}
                onClick={() => {
                    if (readOnly) return;
                    setIsOpen(!isOpen);
                    setSearch('');
                }}
            >
                <span className={`select__value-label${currentLabel === 'Не выбрано' ? ' select__value-label_empty' : ''}`}>
                    {currentLabel}
                </span>
                <span className="select__arrow">{isOpen ? '▴' : '▾'}</span>
            </div>
            {isOpen && dropdownStyle
                ? createPortal(
                    <div className="select__dropdown" style={dropdownStyle} ref={dropdownRef}>
                        <input
                            type="text"
                            className="input input_wf select__search"
                            placeholder="Поиск…"
                            value={search}
                            autoFocus={true}
                            onChange={(event) => setSearch(event.target.value)}
                        />
                        <div className="select__options">
                            {isNull
                                ? <div
                                    className={`select__option${(value === '' || value === null) ? ' select__option_active' : ''}`}
                                    onClick={() => handleSelect('')}
                                >
                                    Не выбрано
                                </div>
                                : null}
                            {filteredOptions.map(([id, meta]) =>
                                <div
                                    key={id}
                                    className={`select__option${String(value) === id ? ' select__option_active' : ''}${meta.header ? ' select__option_header' : ''}${meta.disabled ? ' select__option_disabled' : ''}`}
                                    onClick={() => handleSelect(id, meta.disabled)}
                                >
                                    {meta.label}
                                    {meta.hint ? <span className="select__option-hint"> {meta.hint}</span> : null}
                                </div>
                            )}
                            {filteredOptions.length === 0
                                ? <div className="select__empty">Ничего не найдено</div>
                                : null}
                        </div>
                    </div>,
                    document.body,
                )
                : null}
        </div>
        {errors ? <MessageError errors={errors}/> : null}
    </>;
}
